import { useCallback, useEffect, useState } from 'react';
import {
  learnerApi,
  type ExamAttemptListItem,
  type UserActivitySummary,
  type UserFavouriteRow,
} from '../../../api/learner';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, ErrorState, SegmentedControl, StatusPill } from '@/components/app';
import { getErrorMessage } from '@/lib/errors';
import { EXAM_KIND_LABELS, labelOf } from '@/lib/labels';
import { formatDateTime } from '../format';

interface Props {
  userId: string;
}

type View = 'attempts' | 'favourites';
const VIEWS = [
  { value: 'attempts' as const, label: 'Последние попытки' },
  { value: 'favourites' as const, label: 'Избранное' },
];

const fmt = (n: number) => new Intl.NumberFormat('ru-RU').format(n);

export function UserActivityTab({ userId }: Props) {
  const [view, setView] = useState<View>('attempts');
  const [summary, setSummary] = useState<UserActivitySummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [favourites, setFavourites] = useState<UserFavouriteRow[] | null>(null);
  const [favouritesError, setFavouritesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [favReloadKey, setFavReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSummaryError(null);
    learnerApi
      .userActivity(userId)
      .then((data) => !cancelled && setSummary(data))
      .catch((e: unknown) => !cancelled && setSummaryError(getErrorMessage(e, 'Не удалось загрузить активность')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [userId, reloadKey]);

  useEffect(() => {
    if (view !== 'favourites' || favourites !== null) return;
    let cancelled = false;
    setFavouritesError(null);
    learnerApi
      .userFavourites(userId)
      .then((res) => !cancelled && setFavourites(res.items))
      .catch((e: unknown) => !cancelled && setFavouritesError(getErrorMessage(e, 'Не удалось загрузить избранное')));
    return () => {
      cancelled = true;
    };
  }, [view, userId, favourites, favReloadKey]);

  const retrySummary = useCallback(() => setReloadKey((k) => k + 1), []);
  const retryFavourites = useCallback(() => setFavReloadKey((k) => k + 1), []);

  if (loading) {
    return (
      <div className="flex flex-col gap-2.5" aria-busy>
        <div className="grid grid-cols-3 gap-2.5">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-[92px] rounded-card" />
          ))}
        </div>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (summaryError) return <ErrorState message={summaryError} onRetry={retrySummary} />;
  if (!summary) return <EmptyState title="Нет данных об активности" />;

  return (
    <div className="flex flex-col gap-3.5">
      <div role="group" aria-label="Активность" className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
        <Stat label="Попыток" value={fmt(summary.attempts_total)} />
        <Stat
          label="Ошибок всего"
          value={fmt(summary.wrong_total)}
          hint={`уникальных вопросов: ${fmt(summary.wrong_unique_questions)}`}
        />
        <Stat label="В избранном" value={fmt(summary.favourites_count)} />
      </div>

      <SegmentedControl ariaLabel="Что показать" value={view} onChange={setView} options={VIEWS} className="self-start" />

      {view === 'attempts' &&
        (summary.recent_attempts.length === 0 ? (
          <EmptyState title="Попыток пока нет" className="py-8" />
        ) : (
          <ul className="m-0 flex list-none flex-col overflow-hidden rounded-card border border-border-soft bg-surface p-0 shadow-card">
            {summary.recent_attempts.map((a) => (
              <RecentAttemptRow key={a.id} attempt={a} />
            ))}
          </ul>
        ))}

      {view === 'favourites' &&
        (favouritesError ? (
          <ErrorState compact message={favouritesError} onRetry={() => { setFavourites(null); retryFavourites(); }} />
        ) : favourites === null ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-busy>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : favourites.length === 0 ? (
          <EmptyState title="Избранного нет" className="py-8" />
        ) : (
          <ul className="m-0 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-4">
            {favourites.map((f) => (
              <li key={f.id} className="rounded-xl border border-border-soft bg-surface px-3 py-2 text-xs">
                <div className="font-mono font-semibold">Q#{f.question_id}</div>
                <div className="mt-0.5 text-[11px] tabular-nums text-text-2">{formatDateTime(f.added_at)}</div>
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-card border border-border-soft bg-surface px-4 py-3 shadow-card">
      <span className="text-[13px] text-text-2">{label}</span>
      <b className="font-display text-2xl font-extrabold tabular-nums">{value}</b>
      {hint && <span className="text-xs text-text-2">{hint}</span>}
    </div>
  );
}

function RecentAttemptRow({ attempt }: { attempt: ExamAttemptListItem }) {
  const score = attempt.total_questions > 0 ? `${attempt.correct_count}/${attempt.total_questions}` : '—';

  return (
    <li className="flex items-center justify-between gap-4 border-t border-border-soft px-4 py-3 first:border-t-0">
      <div className="flex min-w-0 items-center gap-2.5">
        <StatusPill size="sm" tone="info">
          {labelOf(EXAM_KIND_LABELS, attempt.kind)}
        </StatusPill>
        {attempt.ref_id !== null && <span className="font-mono text-xs text-text-2">#{attempt.ref_id}</span>}
        <span className="truncate text-xs tabular-nums text-text-2">{formatDateTime(attempt.started_at)}</span>
      </div>
      <div className="flex shrink-0 items-center gap-2.5">
        <span className="text-sm font-semibold tabular-nums">{score}</span>
        {attempt.passed === true && (
          <StatusPill size="sm" tone="pos">
            Сдал
          </StatusPill>
        )}
        {attempt.passed === false && (
          <StatusPill size="sm" tone="neg">
            Не сдал
          </StatusPill>
        )}
        {attempt.abandoned && <StatusPill size="sm">Брошено</StatusPill>}
      </div>
    </li>
  );
}
